"use client";

import { apiGateway } from "@/_old/services/api-gateway";
import { productTypeIds } from "@/core/utils";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  CompetencyUsageAPI,
  CompetencyUsageResponse,
  GetCompetencyUsageParams,
} from "../types";
import { productCompetencyKeys } from "../utils/query-keys";

export async function fetchCompetencyUsage(
  params: GetCompetencyUsageParams,
): Promise<CompetencyUsageAPI> {
  const searchParams = new URLSearchParams();
  searchParams.set("start_date", `${params.startDate}`);
  searchParams.set("end_date", `${params.endDate}`);

  if (params.city && params.city.length > 0) {
    params.city.forEach((city) => searchParams.append("cities[]", city.name));
  }
  if (params.group_id) searchParams.set("group_id", String(params.group_id));
  if (params.type && params.type.length > 0) {
    params.type.forEach((t) =>
      searchParams.append("types[]", String(productTypeIds[t])),
    );
  }
  if (params.status && params.status.length > 0) {
    params.status.forEach((s) => searchParams.append("status[]", String(s)));
  }
  if (params.users && params.users.length > 0) {
    const userIds = params.users.map((user) => user.user?.id).filter(Boolean);
    userIds.forEach((id) => searchParams.append("users[]", String(id)));
  }
  if (params.spaces && params.spaces.length > 0) {
    params.spaces.forEach((space) => searchParams.append("spaces[]", space.id));
  }
  if (params.neighborhoods && params.neighborhoods.length > 0) {
    params.neighborhoods.forEach((n) =>
      searchParams.append("neighborhoods[]", String(n)),
    );
  }

  const { data } = await apiGateway.get<CompetencyUsageResponse>(
    `/api/v2/companies/credits/usage/competency?${searchParams.toString()}`,
  );

  const api = data?.data ?? { competency_type: "daily", usage: [] };
  return {
    competency_type: api.competency_type,
    usage: Array.isArray(api.usage) ? api.usage : [],
  };
}

const isValidDateLike = (v: string | Date | undefined | null) => {
  if (!v) return false;
  if (typeof v === "string") return /^\d{4}-\d{2}-\d{2}$/.test(v);
  return Number.isFinite(new Date(v).getTime());
};

export function useProductCompetency(
  params: GetCompetencyUsageParams,
  options?: UseQueryOptions<CompetencyUsageAPI, Error, CompetencyUsageAPI>,
) {
  const enabled =
    isValidDateLike(params.startDate) && isValidDateLike(params.endDate);

  return useQuery<CompetencyUsageAPI, Error>({
    queryKey: productCompetencyKeys.list(params),
    queryFn: () => fetchCompetencyUsage(params),
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    ...options,
  });
}
